import { isTrackedPath } from "./tracking";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: (...args: unknown[]) => void;
  }
}

/** The fbq queue stub from Meta's base code, before fbevents.js takes it over. */
interface FbqStub {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: FbqStub;
  loaded: boolean;
  version: string;
  disablePushState?: boolean;
}

let pixelId: string | undefined;
let loaded = false;

/** A fresh Meta event ID: one per enquiry, shared by the Pixel and the server. */
export function newMetaEventId(): string {
  return crypto.randomUUID();
}

/** Remembers the Pixel ID. Nothing loads until a tracked page is viewed. */
export function initMetaPixel(id: string | undefined): void {
  pixelId = id;
}

/**
 * Defines fbq and loads fbevents.js once. Calls made before the script arrives
 * wait in fbq.queue. The Pixel's own page view on history changes is off, so a
 * visitor who moves from a public page into the CRM isn't reported there.
 */
function ensureLoaded(): boolean {
  if (!pixelId) return false;
  if (loaded) return true;
  loaded = true;
  const fbq = function () {
    // fbevents.js expects the Arguments object itself, not an array.
    // oxlint-disable-next-line prefer-rest-params
    if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments as unknown as unknown[]);
    // oxlint-disable-next-line prefer-rest-params
    else fbq.queue.push(arguments);
  } as FbqStub;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  fbq.disablePushState = true;
  window.fbq = fbq;
  // Meta's base code also exposes the stub as window._fbq; fbevents.js looks for it.
  // oxlint-disable-next-line no-underscore-dangle
  window._fbq ??= fbq;
  fbq("set", "autoConfig", false, pixelId);
  fbq("init", pixelId);
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.append(script);
  return true;
}

/** Sends a PageView for a tracked page. Does nothing elsewhere or when the Pixel is off. */
export function trackPixelPageView(href = window.location.href): void {
  if (!isTrackedPath(new URL(href).pathname) || !ensureLoaded()) return;
  window.fbq!("track", "PageView", {}, { eventID: newMetaEventId() });
}

/**
 * Fires the browser Pixel's Lead with the same event ID the server sends, so
 * Meta keeps one of the two. Does nothing when the Pixel is off.
 */
export function trackPixelLead(eventId: string): void {
  if (typeof window.fbq === "function") window.fbq("track", "Lead", {}, { eventID: eventId });
}

export function resetMetaPixelForTests(): void {
  pixelId = undefined;
  loaded = false;
  delete window.fbq;
  // oxlint-disable-next-line no-underscore-dangle
  delete window._fbq;
  document.head.querySelectorAll('script[src*="connect.facebook.net"]').forEach((s) => s.remove());
}
